import { useCallback, useMemo } from 'react';
import { useQuery } from 'urql';
import { GetSocialAnalyticsPostsQuery } from '@frontend/gql/social/graphql';
import { getSocialAnalyticsPosts } from '../graphql';
import { QueryVariables } from '../types';
import { Project, Group } from '../components/filters-form/types';

interface UsePostsDataProps {
  clientId: string | undefined;
  tab: string | undefined;
  queryVariables: QueryVariables;
  projects: Project[];
  groups: Group[];
}

type PostType = NonNullable<
  NonNullable<NonNullable<GetSocialAnalyticsPostsQuery['client']>['socialAnalytics']>['posts']
>['results'][number];

export type EnrichedPost = PostType & {
  project: { id: number; name: string } | null;
  group: { id: number; name: string } | null;
};

export type EnrichedPostsData = Omit<GetSocialAnalyticsPostsQuery, 'client'> & {
  client?: Omit<NonNullable<GetSocialAnalyticsPostsQuery['client']>, 'socialAnalytics'> & {
    socialAnalytics?: Omit<
      NonNullable<NonNullable<GetSocialAnalyticsPostsQuery['client']>['socialAnalytics']>,
      'posts'
    > & {
      posts?: Omit<
        NonNullable<NonNullable<NonNullable<GetSocialAnalyticsPostsQuery['client']>['socialAnalytics']>['posts']>,
        'results'
      > & {
        results: EnrichedPost[];
      };
    };
  };
};

export const usePostsData = ({
  clientId,
  tab,
  queryVariables,
  projects,
  groups,
}: UsePostsDataProps): {
  data: EnrichedPostsData | undefined;
  fetching: boolean;
  handleRefresh: () => void;
} => {
  const [{ data: postsData, fetching: fetchingPosts }, refetchPosts] = useQuery({
    query: getSocialAnalyticsPosts,
    variables: {
      clientId: clientId || '',
      startDate: queryVariables.startDate || '',
      endDate: queryVariables.endDate || '',
      postTypes: queryVariables.postTypes || [],
      includeEstimates: queryVariables.includeEstimates || false,
      // includeUnassigned: !queryVariables.includeUnassigned || false,
      networks: queryVariables.networks || [],
      projectIds: queryVariables.projectIds,
      groupIds: queryVariables.groupIds,
      memberIds: queryVariables.memberIds,
      segments: queryVariables.segments,
    },
    pause: !clientId || tab !== 'posts',
  });

  const handleRefresh = useCallback(() => {
    if (tab === 'posts') {
      refetchPosts({ requestPolicy: 'network-only' });
    }
  }, [refetchPosts, tab]);

  // Apply client-side enrichment
  const filteredData = useMemo<EnrichedPostsData | undefined>(() => {
    if (!postsData?.client?.socialAnalytics?.posts?.results) {
      return postsData as EnrichedPostsData;
    }

    // Enrich posts with project and group information
    const enrichedPosts: EnrichedPost[] = postsData.client.socialAnalytics.posts.results.map((post) => {
      let project = projects.find((p) => p.id.toString() === post.projectId);
      let group = groups.find((g) => g.id.toString() === post.groupId);

      if (!project && post.projectId) {
        project = { id: parseInt(post.projectId, 10), name: `Unknown(${post.projectId})` };
      }
      if (!group && post.groupId) {
        group = { id: parseInt(post.groupId, 10), name: `Unknown(${post.groupId})` };
      }
      return {
        ...post,
        project: project ? { id: project.id, name: project.name } : null,
        group: group ? { id: group.id, name: group.name } : null,
      };
    }) as EnrichedPost[];

    // At this point we know postsData, client, socialAnalytics, and posts are all defined
    // since we checked above, so we can safely create our return object
    return {
      ...postsData,
      client: {
        ...postsData.client,
        socialAnalytics: {
          ...postsData.client.socialAnalytics,
          posts: {
            ...postsData.client.socialAnalytics.posts,
            results: enrichedPosts,
            pagination: {
              ...postsData.client.socialAnalytics.posts.pagination,
              totalCount: enrichedPosts.length,
            },
          },
        },
      },
    };
  }, [postsData, projects, groups]);

  return {
    data: filteredData,
    fetching: fetchingPosts,
    handleRefresh,
  };
};
